import React from 'react';

interface ButtonProps {
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  sx?: any;
  className?: string;
  children?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

const Button: React.FC<ButtonProps> = ({
  variant = 'contained',
  size = 'medium',
  color = 'primary',
  onClick,
  disabled = false,
  sx,
  className = '',
  children,
  type = 'button',
}) => {
  const baseClasses = 'font-medium rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses = {
    contained: 'text-white shadow hover:shadow-md',
    outlined: 'border-2 bg-transparent',
    text: 'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800',
  };

  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg',
  };

  const colorClasses = {
    primary: variant === 'contained' 
      ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
      : variant === 'outlined'
      ? 'border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
      : 'text-blue-600',
    secondary: variant === 'contained'
      ? 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500'
      : variant === 'outlined'
      ? 'border-gray-600 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
      : 'text-gray-600',
    error: variant === 'contained'
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
      : variant === 'outlined'
      ? 'border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
      : 'text-red-600',
    warning: variant === 'contained'
      ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
      : variant === 'outlined'
      ? 'border-yellow-600 text-yellow-600 hover:bg-yellow-50'
      : 'text-yellow-600',
    info: variant === 'contained'
      ? 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-400'
      : variant === 'outlined'
      ? 'border-blue-500 text-blue-500 hover:bg-blue-50'
      : 'text-blue-500',
    success: variant === 'contained'
      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
      : variant === 'outlined'
      ? 'border-green-600 text-green-600 hover:bg-green-50'
      : 'text-green-600',
  };

  const customStyles: React.CSSProperties = {
    ...(sx?.mx && { marginLeft: `${sx.mx * 0.25}rem`, marginRight: `${sx.mx * 0.25}rem` }),
    ...(sx?.my && { marginTop: `${sx.my * 0.25}rem`, marginBottom: `${sx.my * 0.25}rem` }),
    ...(sx?.bgcolor && { backgroundColor: sx.bgcolor }),
    ...(sx?.color && { color: sx.color }),
    ...(sx?.borderColor && { borderColor: sx.borderColor }),
    ...(sx && typeof sx === 'object' && !Array.isArray(sx) ? (() => {
      const rest: Record<string, any> = {};
      Object.keys(sx).forEach(key => {
        if (!['mx', 'my', 'bgcolor', 'color', 'borderColor'].includes(key)) {
          rest[key] = sx[key];
        }
      });
      return rest;
    })() : {}),
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${colorClasses[color]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
      style={customStyles}
    >
      {children}
    </button>
  );
};

export default Button;

